import React from 'react'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Button, InputNumber, Modal, Space, Typography } from 'antd'
import { IOrderItem } from 'models/orders'
import { RefundFlowStates, useRefundFlow } from '../RefundFlowModal/RefundFlowStates'
import styles from './DenyItemModal.module.scss'

interface IDenyItemModalProps {
  item: IOrderItem | null
  // Единственный товар заказа: целиком его убирает только отмена всего заказа
  isLastItem: boolean
  // count не передаётся, когда товар убирается целиком. При ошибке должен бросить исключение
  onConfirm: (count?: number) => Promise<void>
  onCancelOrder: () => void
  onClose: () => void
  // Отмена суперадмином не засчитывается продавцу
  isSuperAdmin?: boolean
}

const STATS_WARNING = 'негативно скажется на статистике вашего магазина.'
const STOCK_WARNING =
  'Остаток товара будет обнулён, чтобы его не смогли заказать повторно. Продолжить?'

// Отмена позиции: сколько единиц (если их больше одной) → подтверждение возврата → загрузка → результат
export const DenyItemModal: React.FC<IDenyItemModalProps> = ({
  item,
  isLastItem,
  onConfirm,
  onCancelOrder,
  onClose,
  isSuperAdmin = false
}) => {
  const open = !!item
  const itemCount = item?.count ?? 0
  const hasCountStep = itemCount > 1

  const { step, errorText, runRefund, backToConfirm } = useRefundFlow(open)
  const [isCountStep, setIsCountStep] = React.useState(hasCountStep)
  const [count, setCount] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (open) {
      setIsCountStep(hasCountStep)
      setCount(null)
    }
  }, [item?._id])

  const isLoading = step === 'loading'
  const isCountValid =
    count !== null && Number.isInteger(count) && count >= 1 && count <= itemCount
  const deniedCount = hasCountStep ? count ?? 0 : itemCount
  const remainingCount = itemCount - deniedCount
  const isPartial = remainingCount > 0
  const isOrderCancel = isLastItem && isCountValid && !isPartial

  const goToConfirm = () => {
    if (!isCountValid) return
    if (isOrderCancel) return onCancelOrder()
    setIsCountStep(false)
  }

  const renderCountHint = () => {
    if (count !== null && !isCountValid) {
      return (
        <Typography.Text type='danger'>
          Можно отменить от 1 до {itemCount} шт.
        </Typography.Text>
      )
    }
    if (isOrderCancel) {
      return (
        <Typography.Text type='warning'>
          Это весь товар заказа — заказ будет отменён целиком
        </Typography.Text>
      )
    }
    return (
      <Typography.Text type='secondary'>
        {count !== null && !isPartial
          ? 'Товар будет убран из заказа целиком'
          : `Останется в заказе ${remainingCount} шт.`}
      </Typography.Text>
    )
  }

  return (
    <Modal
      open={open}
      centered
      footer={null}
      width={520}
      closable={!isLoading}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      onCancel={isLoading ? undefined : onClose}
    >
      {item && isCountStep && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Typography.Title level={5}>Сколько единиц отменить?</Typography.Title>
          <Typography.Text type='secondary'>
            {item.product_id?.name ?? 'Удаленный товар'} — в заказе {itemCount} шт.
          </Typography.Text>
          <div style={{ marginTop: 16 }}>
            <Typography.Text>Введите количество от 1 до {itemCount}</Typography.Text>
          </div>
          <Space size={8} style={{ marginTop: 8 }}>
            <InputNumber
              autoFocus
              precision={0}
              value={count}
              onChange={setCount}
              onPressEnter={goToConfirm}
              status={count !== null && !isCountValid ? 'error' : undefined}
              className={styles.countInput}
            />
            <Button onClick={() => setCount(itemCount)}>Все</Button>
          </Space>
          <div style={{ marginTop: 8 }}>{renderCountHint()}</div>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose}>Отмена</Button>
            <Button type='primary' disabled={!isCountValid} onClick={goToConfirm}>
              Далее
            </Button>
          </Space>
        </div>
      )}

      {item && !isCountStep && step === 'confirm' && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <ExclamationCircleOutlined
            style={{ fontSize: 40, color: '#faad14' }}
          />
          <Typography.Title level={5} style={{ marginTop: 12 }}>
            {isPartial ? 'Частичный возврат средств' : 'Убрать товар из заказа'}
          </Typography.Title>
          {isPartial && (
            <Typography.Paragraph>
              Покупателю будет произведён возврат за {deniedCount} шт.
            </Typography.Paragraph>
          )}
          <Typography.Text>
            {!isSuperAdmin &&
              `${isPartial ? 'Отмена' : 'Удаление товара из заказа'} ${STATS_WARNING} `}
            {STOCK_WARNING}
          </Typography.Text>
          <div>
            <Space style={{ marginTop: 16 }}>
              {hasCountStep ? (
                <Button onClick={() => setIsCountStep(true)}>Назад</Button>
              ) : (
                <Button onClick={onClose}>Отмена</Button>
              )}
              <Button
                type='primary'
                onClick={() =>
                  runRefund(() => onConfirm(isPartial ? deniedCount : undefined))
                }
              >
                Продолжить
              </Button>
            </Space>
          </div>
        </div>
      )}

      <RefundFlowStates
        step={step}
        errorText={errorText}
        loadingText='Возвращаем деньги покупателю…'
        successText={
          isPartial
            ? `Отменено ${deniedCount} шт., в заказе осталось ${remainingCount} шт. Остаток товара обнулён`
            : 'Товар исключён из заказа, остаток обнулён'
        }
        onClose={onClose}
        onRetry={backToConfirm}
      />
    </Modal>
  )
}
